import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { XCircle, MessageCircle, Check, X, Clock, AlertCircle } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * Modal reutilizável de cancelamento de consulta.
 * Design: Cards de escolha + Prévia estruturada (Mockup 2)
 *
 * Props:
 *   - consulta: objeto da consulta
 *   - onClose: () => void
 *   - onCancelled: (updatedConsulta) => void — chamado após cancelamento bem-sucedido
 */
export default function CancelarConsultaModal({ consulta, onClose, onCancelled }) {
  const [motivo, setMotivo] = useState(null); // 'cancelado_paciente' | 'cancelado_medico'
  const [modo, setModo] = useState(null); // 'com_msg' | 'sem_msg' (só quando médico)
  const [preview, setPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [processing, setProcessing] = useState(false);

  const isMedico = motivo === 'cancelado_medico';

  // Carrega prévia quando usuário escolhe médico
  useEffect(() => {
    if (!isMedico || !consulta?.id) return;
    let cancelled = false;
    (async () => {
      setLoadingPreview(true);
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`${API}/consultas/${consulta.id}/preview-cancelamento`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (!cancelled) setPreview(res.data);
      } catch (err) {
        if (!cancelled) {
          toast.error('Erro ao carregar prévia');
          setPreview(null);
        }
      } finally {
        if (!cancelled) setLoadingPreview(false);
      }
    })();
    return () => { cancelled = true; };
  }, [isMedico, consulta?.id]);

  const canConfirm = motivo === 'cancelado_paciente' || (motivo === 'cancelado_medico' && modo !== null);

  const handleConfirm = async () => {
    if (!canConfirm || processing) return;
    setProcessing(true);
    try {
      const token = localStorage.getItem('token');
      const payload = {
        status: 'cancelada',
        motivo_cancelamento: motivo,
      };
      if (motivo === 'cancelado_medico') {
        payload.enviar_whatsapp = modo === 'com_msg';
      }
      const res = await axios.put(`${API}/consultas/${consulta.id}`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Consulta cancelada');
      const wr = res.data?.whatsapp_result;
      if (wr?.enviado) toast.success('Notificação WhatsApp enviada ao paciente!');
      else if (motivo === 'cancelado_medico' && modo === 'com_msg' && wr?.motivo) {
        toast.warning(`WhatsApp não enviado: ${wr.motivo}`);
      }
      onCancelled && onCancelled(res.data);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao cancelar consulta');
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" data-testid="cancelar-modal">
      <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full p-6 animate-in fade-in zoom-in duration-200 max-h-[90vh] overflow-y-auto">
        <div className="text-center mb-5">
          <div className="w-14 h-14 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-3">
            <XCircle className="w-7 h-7 text-red-600" />
          </div>
          <h3 className="text-xl font-semibold text-gray-900">Cancelar Consulta</h3>
        </div>

        {/* 1. Quem está cancelando? */}
        <div className="mb-5">
          <label className="text-sm font-medium text-gray-700 mb-2 block">Quem está cancelando?</label>
          <div className="grid grid-cols-2 gap-2">
            <label
              className={`flex items-center gap-2 p-3 border-2 rounded-lg cursor-pointer transition-colors ${
                motivo === 'cancelado_paciente' ? 'border-red-400 bg-red-50' : 'border-gray-200 hover:border-gray-300'
              }`}
              data-testid="motivo-paciente"
            >
              <input
                type="radio"
                checked={motivo === 'cancelado_paciente'}
                onChange={() => { setMotivo('cancelado_paciente'); setModo(null); }}
                className="text-red-600"
              />
              <span className="text-sm font-medium">Paciente</span>
            </label>
            <label
              className={`flex items-center gap-2 p-3 border-2 rounded-lg cursor-pointer transition-colors ${
                motivo === 'cancelado_medico' ? 'border-red-400 bg-red-50' : 'border-gray-200 hover:border-gray-300'
              }`}
              data-testid="motivo-medico"
            >
              <input
                type="radio"
                checked={motivo === 'cancelado_medico'}
                onChange={() => setMotivo('cancelado_medico')}
                className="text-red-600"
              />
              <span className="text-sm font-medium">Médico / Clínica</span>
            </label>
          </div>
        </div>

        {/* 2. Modo de notificação (só para médico/clínica) */}
        {isMedico && (
          <div className="mb-5">
            <label className="text-sm font-medium text-gray-700 mb-2 block">Deseja notificar o paciente?</label>
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => setModo('com_msg')}
                className={`p-4 border-2 rounded-lg text-left transition-all ${
                  modo === 'com_msg' ? 'border-green-500 bg-green-50 ring-2 ring-green-200' : 'border-gray-200 hover:border-gray-300'
                }`}
                data-testid="modo-com-msg"
              >
                <div className="flex items-center gap-2 mb-1">
                  <MessageCircle className={`w-4 h-4 ${modo === 'com_msg' ? 'text-green-600' : 'text-gray-400'}`} />
                  <span className={`text-sm font-bold ${modo === 'com_msg' ? 'text-green-900' : 'text-gray-700'}`}>Com mensagem</span>
                  {modo === 'com_msg' && <Check className="w-4 h-4 text-green-600 ml-auto" />}
                </div>
                <p className="text-[11px] text-gray-500">Envia WhatsApp ao paciente com sugestões</p>
              </button>

              <button
                type="button"
                onClick={() => setModo('sem_msg')}
                className={`p-4 border-2 rounded-lg text-left transition-all ${
                  modo === 'sem_msg' ? 'border-gray-500 bg-gray-100 ring-2 ring-gray-200' : 'border-gray-200 hover:border-gray-300'
                }`}
                data-testid="modo-sem-msg"
              >
                <div className="flex items-center gap-2 mb-1">
                  <X className={`w-4 h-4 ${modo === 'sem_msg' ? 'text-gray-700' : 'text-gray-400'}`} />
                  <span className={`text-sm font-bold ${modo === 'sem_msg' ? 'text-gray-900' : 'text-gray-700'}`}>Sem notificação</span>
                  {modo === 'sem_msg' && <Check className="w-4 h-4 text-gray-700 ml-auto" />}
                </div>
                <p className="text-[11px] text-gray-500">Cancela sem avisar o paciente</p>
              </button>
            </div>

            {/* Aviso se não puder enviar */}
            {modo === 'com_msg' && preview && !preview.pode_enviar && (
              <div className="mt-3 flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <AlertCircle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                <div className="text-xs text-amber-800">
                  <strong>Não será possível enviar:</strong> {preview.motivo_indisponivel}
                </div>
              </div>
            )}

            {/* Prévia da mensagem */}
            {modo === 'com_msg' && (
              <div className="mt-4 rounded-lg border border-gray-200 overflow-hidden" data-testid="preview-mensagem">
                <div className="px-3 py-2 bg-gray-900 flex items-center gap-2">
                  <MessageCircle className="w-3.5 h-3.5 text-green-400" />
                  <span className="text-[11px] uppercase tracking-wider font-bold text-gray-300">Prévia — WhatsApp</span>
                  {preview?.telefone && (
                    <span className="ml-auto text-[10px] text-gray-400">→ {preview.telefone}</span>
                  )}
                </div>
                {loadingPreview ? (
                  <div className="p-6 text-center text-xs text-gray-400">Carregando prévia...</div>
                ) : preview ? (
                  <>
                    <div className="p-3 bg-white">
                      <pre className="text-xs text-gray-700 whitespace-pre-wrap font-sans leading-relaxed">{preview.mensagem}</pre>
                    </div>
                    <div className="px-3 py-2 bg-blue-50 border-t border-blue-100">
                      <p className="text-[10px] font-bold text-blue-700 uppercase tracking-wider mb-1 flex items-center gap-1">
                        <Clock className="w-3 h-3" /> Datas que serão sugeridas ao paciente
                      </p>
                      {preview.slots && preview.slots.length > 0 ? (
                        <div className="grid grid-cols-1 gap-0.5">
                          {preview.slots.map((s, i) => (
                            <div key={i} className="text-xs text-blue-800 font-medium">→ {s.display}</div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-xs text-blue-700 italic">Nenhum horário disponível encontrado</div>
                      )}
                    </div>
                  </>
                ) : (
                  <div className="p-4 text-center text-xs text-red-500">Não foi possível carregar a prévia</div>
                )}
              </div>
            )}
          </div>
        )}

        <div className="flex gap-3 mt-5">
          <Button
            variant="outline"
            onClick={onClose}
            className="flex-1 py-2.5"
            disabled={processing}
            data-testid="cancelar-modal-voltar"
          >
            Voltar
          </Button>
          <Button
            onClick={handleConfirm}
            className="flex-1 py-2.5 bg-red-600 hover:bg-red-700 text-white disabled:bg-gray-300 disabled:cursor-not-allowed"
            disabled={!canConfirm || processing}
            data-testid="cancelar-modal-confirmar"
          >
            {processing ? (
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
            ) : (
              <XCircle className="w-4 h-4 mr-2" />
            )}
            Confirmar Cancelamento
          </Button>
        </div>
      </div>
    </div>
  );
}
